import * as React from 'react';

import type { W_Equipment_FindAllForCompanyList_VM } from 'ab-api/web/equipment/findAllForCompanyList';

import { bemElement } from 'af-utils/bem.util';

import ReportBlockField from './Field';
import { useReportTypePreviewHighlightContext } from '../../ReportType/Context/ReportTypePreviewHighlight';
import type { ReportBlockFieldFormModel } from '../formModel';

interface OwnProps {
	filterVisibleToCustomer: boolean;
	formField: string;
	formValues?: Record<string, string>[];
	highlightVisibleToCustomer?: boolean;
	index?: number;
	onCalculatedFieldChange: (id: string) => void;
	onFieldValueChange?: (id: string, value: string, index: number | undefined) => void;
	listOfEquipment?: Nullable<W_Equipment_FindAllForCompanyList_VM>;
	reportBlockFieldIds: Nullable<string[]>;
	reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; };
}

type Props = OwnProps;

const Block: React.FC<Props> = (props: Props) => {
	const {
		reportBlockFieldIds,
		formField,
		filterVisibleToCustomer,
		highlightVisibleToCustomer,
		onFieldValueChange,
		onCalculatedFieldChange,
		listOfEquipment,
		formValues,
		index,
		reportFieldsByIdMap,
	} = props;

	const { virtualIds: highlightedVirtualIds, focusedVirtualId } = useReportTypePreviewHighlightContext();

	const filterVisibleField = React.useCallback((fieldId: string) => {
		const field = reportFieldsByIdMap[fieldId];
		if (!field) {
			throw new Error('Missing field');
		}
		return field.isVisibleToCustomer;
	}, [reportFieldsByIdMap]);

	const renderField = React.useCallback((fieldId: string) => {
		const field = reportFieldsByIdMap[fieldId];
		if (!field) {
			throw new Error('Missing field');
		}
		const id = `report-block-field-${field.virtualId}`;
		const isCurrentlyHighlighted = highlightedVirtualIds[field.virtualId] ?? false;

		return (
			<ReportBlockField
				field={field}
				formField={formField}
				formValues={formValues}
				highlightVisibleToCustomer={highlightVisibleToCustomer}
				id={id}
				isFocused={focusedVirtualId === field.virtualId}
				isHighlighted={isCurrentlyHighlighted}
				key={field.virtualId}
				listOfEquipment={listOfEquipment}
				onCalculatedFieldChange={onCalculatedFieldChange}
				onFieldValueChange={onFieldValueChange}
				repeatIndex={index}
			/>
		);
	}, [reportFieldsByIdMap,
		formField,
		formValues,
		highlightVisibleToCustomer,
		listOfEquipment,
		onCalculatedFieldChange,
		onFieldValueChange,
		index,
		focusedVirtualId,
		highlightedVirtualIds]
	);

	const fields = React.useMemo(() => {
		const _fields = (reportBlockFieldIds ?? []);
		return filterVisibleToCustomer
			? _fields.filter(filterVisibleField)
			: _fields;
	}, [filterVisibleField, filterVisibleToCustomer, reportBlockFieldIds]);

	const className = bemElement('report-block', 'array-elements', {
		'settings-preview': true,
	});

	return (
		<div className={className}>
			{fields.map(renderField)}
		</div>
	);
};

export default React.memo(Block);
